import { NavLink } from 'react-router-dom';
import { useSession } from '../hooks/useSession';
import { lessonKey } from '../lib/curriculum';
import { Icon } from './Icon';

const TOOLS = [
  { to: '/tools/grader', label: 'Listing checker', path: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
  { to: '/tools/calculator', label: 'Pricing calculator', path: 'M9 7h6m-6 4h6m-6 4h2M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z' },
  { to: '/tools/photos', label: 'Photo standard', path: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z' },
  { to: '/checklists', label: 'Checklists', path: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
];

/**
 * The ShipIndia box mark — the real brand asset, shared with the mobile app.
 *
 * This used to be a hand-drawn hexagon path filled white, which read as a blank shape
 * rather than the logo. The mark is an isometric parcel with an S cut through it, and it
 * carries the orange, so it cannot be reproduced as a single-colour path.
 */
function Mark() {
  return (
    <img
      src="/shipindia-mark.png"
      width={26}
      height={26}
      alt=""
      aria-hidden="true"
      style={{ display: 'block', objectFit: 'contain' }}
    />
  );
}

export function Sidebar({ open = false, onNavigate }: { open?: boolean; onNavigate?: () => void }) {
  const { content, progress } = useSession();
  const tracks = content?.tracks ?? [];

  return (
    // `data-open` drives the off-canvas drawer below 900px; on desktop the rail is
    // always visible and the attribute is inert.
    <aside className="vu-side" data-open={open ? 'true' : 'false'} onClick={onNavigate}>
      <div className="vu-brand">
        <div className="vu-brand-row">
          <Mark />
          <span className="vu-brand-name">ShipIndia</span>
        </div>
        <div className="vu-brand-sub">Vendor University</div>
        <div className="vu-brand-tag">Sell India to the world</div>
      </div>

      <div className="vu-navgroup">
        <div className="vu-navlabel">Start here</div>
        <NavLink to="/" end className="vu-navitem vu-navitem-primary">
          <Icon path="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" size={17} />
          <span className="vu-navitem-label">Dashboard</span>
        </NavLink>
        <NavLink to="/path" className="vu-navitem">
          <Icon path="M9 18l6-6-6-6" size={17} />
          <span className="vu-navitem-label">The guided path</span>
        </NavLink>
      </div>

      <div className="vu-navgroup">
        <div className="vu-navlabel">Tracks</div>
        {tracks.map((track) => {
          const done = track.lessons.filter((l) => progress.done[lessonKey(track.id, l.id)]).length;
          return (
            <NavLink key={track.id} to={`/track/${track.id}`} className="vu-navitem">
              <Icon path={track.icon} size={17} />
              <span className="vu-navitem-label">{track.name}</span>
              <span className="vu-navitem-badge">
                {done}/{track.lessons.length}
              </span>
            </NavLink>
          );
        })}
      </div>

      <div className="vu-navgroup">
        <div className="vu-navlabel">Tools</div>
        {TOOLS.map((tool) => (
          <NavLink key={tool.to} to={tool.to} className="vu-navitem">
            <Icon path={tool.path} size={17} />
            <span className="vu-navitem-label">{tool.label}</span>
          </NavLink>
        ))}
      </div>

      <div className="vu-support">
        Stuck on something specific? Your account manager answers within one working day.
        <br />
        <a href="mailto:vendors@shipindia.co">vendors@shipindia.co</a>
      </div>
    </aside>
  );
}
